import React, { useEffect, useMemo } from 'react';
import { TourStep } from '@shared/components/Tour';
import { HotelTourStepContent } from './HotelTourStepContent';
import type { AccommodationCard } from '../../Accommodations.types';
import { useAccommodationsTourStepContext } from './AccommodationsTourStepContext';
export type HotelTourStepProps = Readonly<{
  hotel: AccommodationCard;
  hotelIndex: number;
  children: React.ReactElement;
}>;

/**
 * HotelTourStep - Wraps TourStep and registers the hotel step with the AccommodationsTourProvider
 *
 * This component automatically registers itself with the hotel tour step context,
 * allowing the AccommodationsTourProvider to discover all hotel steps dynamically.
 */
export const HotelTourStep: React.FC<HotelTourStepProps> = ({ hotel, hotelIndex, children }) => {
  const context = useAccommodationsTourStepContext();
  const registerStep = context.registerStep;

  const stepName = `Hotel-${hotelIndex}`;

  const shouldRegisterStep = useMemo(() => {
    if (!hotel.autoSuggestionRecommendation) {
      return false;
    }
    return !!hotel.autoSuggestionRecommendation.whyThisHotelIsAGreatPick;
  }, [hotel.autoSuggestionRecommendation]);

  useEffect(() => {
    registerStep('hotel', stepName, !shouldRegisterStep);
  }, [registerStep, stepName, shouldRegisterStep]);

  const tooltip = useMemo(() => <HotelTourStepContent hotel={hotel} />, [hotel]);

  return (
    <TourStep
      stepName={stepName}
      tourName={context.tourName}
      tooltip={tooltip}
    >
      {children}
    </TourStep>
  );
};

HotelTourStep.displayName = 'HotelTourStep';
